/**
 * "O que fazer agora" da lista: cartoes compactos, lado a lado, cada um com
 * UMA acao que recorta a propria lista ou abre a tela que resolve.
 *
 * Compacto de proposito: esta pagina e operacional, a tabela e o centro. O
 * bloco existe para o olho parar no dinheiro parado antes de rolar. Quando
 * as facetas sao parciais (backend sem facetas), o rodape diz que os avisos
 * foram lidos so dos leads carregados.
 */

import { Badge, Button, Footnote, Icon, LinkButton, SectionCard, type BadgeTone } from '../../../components/ui';
import type { AcaoDoInsight, InsightDaLista, TomDoInsight } from '../insights';

const TOM: Readonly<Record<TomDoInsight, { readonly label: string; readonly tone: BadgeTone; readonly borda: string; readonly icone: string }>> = {
  alerta: { label: 'Atenção', tone: 'warning', borda: 'border-status-warning/50', icone: 'bg-status-warning/15 text-status-warning' },
  oportunidade: { label: 'Oportunidade', tone: 'good', borda: 'border-status-good/40', icone: 'bg-status-good/10 text-status-good' },
  projecao: { label: 'Nos próximos minutos', tone: 'info', borda: 'border-info/40', icone: 'bg-info/10 text-info' },
  bom: { label: 'Tudo em ordem', tone: 'good', borda: 'border-line', icone: 'bg-ink/5 text-ink-muted' },
};

export function OQueFazerAgoraLeads({
  insights,
  carregando,
  parciais,
  onAcao,
}: {
  readonly insights: readonly InsightDaLista[];
  readonly carregando: boolean;
  readonly parciais: boolean;
  readonly onAcao: (acao: AcaoDoInsight) => void;
}): JSX.Element {
  const alertas = insights.filter((i) => i.tom === 'alerta').length;
  const oportunidades = insights.filter((i) => i.tom === 'oportunidade').length;

  return (
    <SectionCard
      id="o-que-fazer-agora"
      title="O que fazer agora"
      icon="clique"
      description="Lido dos leads do período. Cada cartão recorta esta lista ou abre a tela que resolve."
      aside={
        <>
          {alertas > 0 ? (
            <Badge tone="warning" dot>
              {alertas} {alertas === 1 ? 'atenção' : 'atenções'}
            </Badge>
          ) : null}
          {oportunidades > 0 ? (
            <Badge tone="good" dot>
              {oportunidades} {oportunidades === 1 ? 'oportunidade' : 'oportunidades'}
            </Badge>
          ) : null}
        </>
      }
    >
      {carregando ? (
        <ul aria-hidden="true" className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-28 animate-pulse rounded-xl border border-line bg-elevated" />
          ))}
        </ul>
      ) : (
        <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {insights.map((i, indice) => {
            const t = TOM[i.tom];
            return (
              <li key={i.key} className={`flex h-full flex-col gap-2 rounded-xl border bg-elevated px-4 py-3 ${t.borda}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className={`grid h-7 w-7 place-items-center rounded-lg ${t.icone}`}>
                      <Icon name={i.icone} size="sm" />
                    </span>
                    <Badge tone={t.tone}>{t.label}</Badge>
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{indice + 1}º</span>
                </div>
                <h3 className="text-sm font-semibold leading-snug text-ink">{i.titulo}</h3>
                <p className="flex-1 text-xs leading-relaxed text-ink-muted">{i.texto}</p>
                {i.acao !== null && i.rotuloAcao !== null ? (
                  <div>
                    {i.acao.kind === 'rota' ? (
                      <LinkButton to={i.acao.to} variant="secondary" size="sm" iconRight="seta-dir">
                        {i.rotuloAcao}
                      </LinkButton>
                    ) : (
                      <Button variant="secondary" size="sm" iconRight="seta-dir" onClick={() => (i.acao === null ? undefined : onAcao(i.acao))}>
                        {i.rotuloAcao}
                      </Button>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3">
        <Footnote>
          {parciais ? 'Avisos lidos só dos leads já carregados nesta página. ' : ''}
          Nenhum aviso sugere falar com quem ainda não saiu: a régua só toca em quem já abandonou o checkout ou teve o plano vencido. É isso que sustenta a comissão.
        </Footnote>
      </div>
    </SectionCard>
  );
}
